import { useTranslation } from 'react-i18next';
import type { ChargeOutcome } from '@evimo/types/constants/session/session.labels';
import type { SessionQualification } from '@evimo/types/enums/session-qualification';
import {
  isCompletedSuccessfully,
  qualificationLabelKey,
  qualificationTagColor,
  qualificationTooltipKey,
} from '~/app/components/session/utils/qualificationDisplay';
import type { SessionChargeResponseDto } from '@evimo/types/api/session/session.response.dto';
import {
  chargeEndLabelKey,
  faultLabelKey,
  humanizeStoredToken,
  readingKind,
} from '@evimo/types/constants/session/session.labels';
import {
  displayName,
  formatClock,
  formatDayClock,
  formatEnergy,
  formatMoney,
  formatReadingValue,
} from '~/app/components/session/utils';

export function useActivityWords() {
  const { t, i18n } = useTranslation('sessions');
  const locale = i18n.language;

  return {
    t,
    locale,
    name: (value: string | null | undefined) => displayName(value, '-'),
    clock: (iso: string | null) => formatClock(iso, locale, '-'),
    dayClock: (iso: string) => formatDayClock(iso, locale),
    energy: (kwh: number | null) => formatEnergy(kwh, locale),
    money: (amount: number) => formatMoney(amount, locale),
    readingValue: (value: number, unit: string) =>
      formatReadingValue(value, unit, locale),
    minutes: (count: number) => t('minutes', { count: Math.round(count) }),
    duration: (startedAt: string | null, stoppedAt: string | null) => {
      if (!startedAt || !stoppedAt) return t('unknown');
      const start = new Date(startedAt).getTime();
      const stop = new Date(stoppedAt).getTime();
      if (isNaN(start) || isNaN(stop)) return t('unknown');
      const diff = Math.round((stop - start) / 60_000);
      if (diff < 0) return t('unknown');
      const h = Math.floor(diff / 60);
      const m = diff % 60;
      if (h > 0) {
        return t('hours', { hours: h, minutes: m });
      }
      return t('minutes', { count: m });
    },
    status: (value: string) =>
      value
        ? t(`status.${value}`, { defaultValue: humanizeStoredToken(value) })
        : '-',
    severity: (value: string) =>
      value
        ? t(`severity.${value}`, { defaultValue: humanizeStoredToken(value) })
        : '-',
    endLabel: (outcome: ChargeOutcome) =>
      outcome ? t(`end.${chargeEndLabelKey(outcome)}`) : '-',
    sessionStatusLabel: (
      charge: Pick<
        SessionChargeResponseDto,
        'outcome' | 'success' | 'qualification'
      >,
    ) => {
      if (
        isCompletedSuccessfully({
          outcome: charge.outcome,
          success: charge.success,
          qualification: charge.qualification as SessionQualification | null,
        })
      ) {
        return t('end.completedSuccessfully');
      }
      return charge.outcome
        ? t(`end.${chargeEndLabelKey(charge.outcome)}`)
        : t('unknown');
    },
    qualificationLabel: (value: SessionQualification | null | undefined) =>
      value ? t(`qualification.${qualificationLabelKey(value)}`) : t('unknown'),
    qualificationTooltip: (value: SessionQualification | null | undefined) =>
      value ? t(`qualification.${qualificationTooltipKey(value)}`) : '',
    qualificationColor: (value: SessionQualification) =>
      qualificationTagColor(value),
    problem: (faultType: string) => {
      if (!faultType) return '-';
      const key = faultLabelKey(faultType);
      return key ? t(`faults.${key}`) : humanizeStoredToken(faultType);
    },
    readingName: (parameter: string) =>
      parameter ? humanizeStoredToken(parameter) : '-',
    kind: (parameter: string, measureType: string) =>
      parameter && measureType
        ? t(`kind.${readingKind(parameter, measureType)}`)
        : '-',
  };
}
